import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Loader2, Info } from "lucide-react";
import {
  serviceCollectionMethodValues,
  serviceCoverageModeValues,
} from "@shared/contracts/billing";
import type { Client, Product } from "@db/schema";

interface CustomFieldConfig {
  key: string;
  label: string;
  type: "text" | "select" | "boolean";
  options?: string[];
  required?: boolean;
}

type ProductWithConfig = Omit<Product, "customFieldsConfig"> & {
  customFieldsConfig: { fields: CustomFieldConfig[] } | null;
  defaultServiceTypeId: string | null;
};

const serviceFormSchema = z.object({
  clientId: z.string().min(1, "Client is required"),
  productId: z.string().min(1, "Product is required"),
  contractId: z.string().optional(),
  budgetId: z.string().optional(),
  serviceTypeId: z.string().optional(),
  defaultServiceItemId: z.string().optional(),
  customName: z.string().optional(),
  price: z.string().optional(),
  recurrence: z.enum(["monthly", "annual"]).default("monthly"),
  nextRenewalDate: z.string().min(1, "Renewal date is required"),
  coverageMode: z.enum(serviceCoverageModeValues).default("ad_hoc_only"),
  collectionMethod: z.enum(serviceCollectionMethodValues).default("stripe_invoice"),
  notes: z.string().optional(),
  initiallyPaid: z.boolean().default(true),
  customFieldsData: z.record(z.union([z.string(), z.boolean()])).optional(),
});

type ServiceFormValues = z.infer<typeof serviceFormSchema>;

interface ServiceFormProps {
  defaultValues?: Partial<ServiceFormValues>;
  onSubmit: (data: ServiceFormValues) => void;
  isPending?: boolean;
  fixedClientId?: string;
}

export function ServiceForm({
  defaultValues,
  onSubmit,
  isPending,
  fixedClientId,
}: ServiceFormProps) {
  const isEditing = !!defaultValues?.clientId;

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/billing/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: products } = useQuery<ProductWithConfig[]>({
    queryKey: ["/api/billing/products"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: contracts } = useQuery<Array<{ id: string; name: string; client: { name: string } }>>({
    queryKey: ["/api/contracts"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: budgets } = useQuery<
    Array<{ id: string; name: string; client: { id: string; name: string }; contract?: { id: string; name: string } | null; service?: { id: string; customName?: string | null; product?: { name: string } } | null }>
  >({
    queryKey: ["/api/budgets"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: serviceTypes } = useQuery<Array<{ id: string; label: string }>>({
    queryKey: ["/api/service-types"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: serviceItems } = useQuery<
    Array<{ id: string; name: string; budget: { id: string; name: string } }>
  >({
    queryKey: ["/api/service-items"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceFormSchema),
    defaultValues: {
      clientId: fixedClientId ?? "",
      productId: "",
      contractId: "",
      budgetId: "",
      serviceTypeId: "",
      defaultServiceItemId: "",
      customName: "",
      price: "",
      recurrence: "monthly",
      nextRenewalDate: "",
      coverageMode: "ad_hoc_only",
      collectionMethod: "stripe_invoice",
      notes: "",
      initiallyPaid: true,
      customFieldsData: {},
      ...defaultValues,
    },
  });

  const selectedProductId = form.watch("productId");

  const selectedProduct = useMemo(() => {
    if (!products || !selectedProductId) return null;
    return products.find((p) => p.id === selectedProductId) ?? null;
  }, [products, selectedProductId]);

  // Auto-fill name, price, and recurrence when a product is selected (only for new services)
  useEffect(() => {
    if (!selectedProduct || isEditing) return;

    const currentCustomName = form.getValues("customName");
    const currentPrice = form.getValues("price");

    // Only auto-fill if user hasn't manually typed values
    if (!currentCustomName) {
      form.setValue("customName", selectedProduct.name);
    }
    if (!currentPrice) {
      form.setValue("price", selectedProduct.defaultPrice);
    }
    form.setValue("recurrence", selectedProduct.defaultRecurrence);
    if (!form.getValues("serviceTypeId") && selectedProduct.defaultServiceTypeId) {
      form.setValue("serviceTypeId", selectedProduct.defaultServiceTypeId);
    }
  }, [selectedProduct, isEditing, form]);

  // Dynamic custom fields from the selected product
  const customFieldConfigs: CustomFieldConfig[] = useMemo(() => {
    return selectedProduct?.customFieldsConfig?.fields ?? [];
  }, [selectedProduct]);
  const selectedBudgetId = form.watch("budgetId");
  const filteredBudgets = useMemo(() => {
    if (!budgets) return [];
    const clientId = fixedClientId || form.getValues("clientId");
    return clientId ? budgets.filter((budget) => budget.client.id === clientId) : budgets;
  }, [budgets, fixedClientId, form]);
  const filteredServiceItems = useMemo(
    () =>
      (serviceItems ?? []).filter((item) =>
        selectedBudgetId ? item.budget.id === selectedBudgetId : true,
      ),
    [serviceItems, selectedBudgetId],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Client selector — hidden if fixed */}
        {!fixedClientId && (
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Client *</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(clients ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="productId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product *</FormLabel>
              <Select
                value={field.value}
                onValueChange={(val) => {
                  // Reset auto-filled values when product changes (for new services)
                  if (!isEditing) {
                    form.setValue("customName", "");
                    form.setValue("price", "");
                    form.setValue("customFieldsData", {});
                  }
                  field.onChange(val);
                }}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(products ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="serviceTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Type</FormLabel>
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) =>
                    field.onChange(value === "none" ? "" : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No service type</SelectItem>
                    {(serviceTypes ?? []).map((serviceType) => (
                      <SelectItem key={serviceType.id} value={serviceType.id}>
                        {serviceType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contractId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contract</FormLabel>
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) =>
                    field.onChange(value === "none" ? "" : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Optional contract" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No contract</SelectItem>
                    {(contracts ?? []).map((contract) => (
                      <SelectItem key={contract.id} value={contract.id}>
                        {contract.name} · {contract.client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="budgetId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Linked Budget</FormLabel>
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) => {
                    const nextValue = value === "none" ? "" : value;
                    field.onChange(nextValue);
                    if (!nextValue) {
                      form.setValue("defaultServiceItemId", "");
                    }
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Optional tracked budget" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No budget</SelectItem>
                    {filteredBudgets.map((budget) => (
                      <SelectItem key={budget.id} value={budget.id}>
                        {budget.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultServiceItemId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Default Service Item</FormLabel>
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) =>
                    field.onChange(value === "none" ? "" : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Optional tracked item" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No default service item</SelectItem>
                    {filteredServiceItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="customName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Service Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Override product name for this service"
                  {...field}
                />
              </FormControl>
              <FormDescription className="flex items-center gap-1 text-xs">
                <Info className="h-3 w-3" />
                Pre-filled from product. Enter a custom name if this instance differs.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price (ex. VAT)</FormLabel>
                <FormControl>
                  <Input placeholder="99.00" type="number" step="0.01" {...field} />
                </FormControl>
                <FormDescription className="flex items-center gap-1 text-xs">
                  <Info className="h-3 w-3" />
                  Custom price for this client. Defaults from product.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="recurrence"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Recurrence</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nextRenewalDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Next Renewal Date *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="coverageMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Coverage Mode</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {serviceCoverageModeValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="collectionMethod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Collection Method</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {serviceCollectionMethodValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Initially Paid toggle — only shown when creating */}
        {!isEditing && (
          <FormField
            control={form.control}
            name="initiallyPaid"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm font-medium">Already Paid</FormLabel>
                  <FormDescription className="text-xs">
                    {field.value
                      ? "Service will be active immediately. Billing starts at next renewal."
                      : "Service will be pending payment. An invoice will be created to collect payment before activation."}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        {/* Dynamic custom fields from product config */}
        {customFieldConfigs.length > 0 && (
          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-medium">Service Details</p>
            {customFieldConfigs.map((cfg) => (
              <FormField
                key={cfg.key}
                control={form.control}
                name={`customFieldsData.${cfg.key}`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">
                      {cfg.label}{cfg.required ? " *" : ""}
                    </FormLabel>
                    <FormControl>
                      {cfg.type === "select" && cfg.options ? (
                        <Select
                          value={(field.value as string) ?? ""}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${cfg.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {cfg.options.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : cfg.type === "boolean" ? (
                        <div className="flex items-center">
                          <Switch
                            checked={!!field.value}
                            onCheckedChange={field.onChange}
                          />
                          <span className="ml-2 text-sm text-muted-foreground">
                            {field.value ? "Yes" : "No"}
                          </span>
                        </div>
                      ) : (
                        <Input
                          placeholder={cfg.label}
                          value={(field.value as string) ?? ""}
                          onChange={field.onChange}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        )}

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Internal Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Internal notes..."
                  className="min-h-[60px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Update Service" : "Add Service"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
